import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import TextButton from "../../../elements/V2/Buttons/TextButton"
import ClickableCardLink from "../../../elements/V2/ClickableCardLink"
import IconInfoBlock from "../../../elements/V2/IconInfoBlock"
import ArrowIcon from "../../../elements/V2/Icons/ArrowIcon"
import LayoutItem from "../../Layouts/LayoutItem"

import { PriceBadgeProps } from "@components/elements/V2/priceBadge"
import Typography from "@components/elements/V2/Typography"
import {
  getAccentColor,
  getBackgroundColorClass,
  getBorderColorClass,
} from "@utils/V2/color"
import React from "react"

type BaseProps = {
  backgroundColor: Color
  iconUrl?: string
  iconAlt?: string
  titleText: string
  titleSize?: "small" | "large"
  subTitleText?: string
  bodyHeader?: string
  descriptionText: string
  size: "large" | "medium" | "small"
  priceBadge?: PriceBadgeProps
  mobileGridOrder?: number
  tags?: {
    text: string
    textColor: Color
    secondaryText?: string
    secondaryTextColor?: Color
    backgroundColor: Color
  }[]
}

type LargeProps = BaseProps &
  Partial<{
    // size: "large"
    buttonText?: string
    buttonLink?: string | YocoCom.YocoLink
    trackingEvent?: string
    trackingEventKey?: string
    trackingEventValue?: string
  }>

type MediumProps = BaseProps & {
  // size: "medium"
}

type Props = LargeProps & MediumProps

const Intro = ({
  backgroundColor,
  descriptionText,
  titleText,
  titleSize = "small",
  subTitleText,
  bodyHeader,
  priceBadge,
  iconUrl,
  iconAlt,
  size,
  buttonText,
  buttonLink,
  mobileGridOrder,
  tags,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  ...props
}: Props) => {
  const hasTags = !!tags?.length
  const isClickable = size === "large" && !!buttonLink
  const hasTagsOrButton =
    (size === "large" && buttonText && buttonLink) || hasTags

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      animateOnScroll
      className={classNames(
        getBackgroundColorClass(backgroundColor),
        "p-lg rounded-md relative col-span-full flex flex-col justify-between gap-16",
        size === "small"
          ? "md:col-span-3 lg:col-span-3"
          : "md:col-span-3 lg:col-span-4",
        isClickable &&
          "group cursor-pointer transition-shadow duration-300 hover:shadow-xl"
      )}
      {...props}
    >
      <IconInfoBlock
        size={size}
        textColor={getAccentColor(backgroundColor)}
        bodyHeader={bodyHeader}
        bodyText={descriptionText}
        titleText={titleText}
        titleSize={titleSize}
        subTitleText={subTitleText}
        priceBadge={priceBadge}
        iconUrl={iconUrl}
        iconAlt={iconAlt}
      />

      {hasTagsOrButton && (
        <div className="flex flex-col gap-8">
          {hasTags && (
            <div className="flex flex-wrap items-center gap-8">
              {tags.map((tag) => (
                <div
                  key={`${tag.text}-${tag.secondaryText ?? ""}`}
                  className={classNames(
                    "flex w-fit items-center gap-10 rounded-8 border border-opacity-10 bg-opacity-10 px-10 py-[6px]",
                    getBackgroundColorClass(tag.backgroundColor),
                    getBorderColorClass(tag.backgroundColor)
                  )}
                >
                  <Typography
                    weight="medium"
                    size="subscript-md"
                    font="grotesk"
                    color={tag.textColor}
                    text={tag.text}
                  />

                  {tag.secondaryText && (
                    <Typography
                      weight="book"
                      size="subscript-sm"
                      font="grotesk"
                      color={tag.secondaryTextColor ?? tag.textColor}
                      text={tag.secondaryText}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {size === "large" && buttonText && buttonLink && (
            <TextButton
              color={getAccentColor(backgroundColor)}
              text={buttonText}
              style="icon"
              icon={<ArrowIcon />}
              decorative={true}
            />
          )}
        </div>
      )}

      {isClickable && buttonLink && (
        <ClickableCardLink
          link={buttonLink}
          ariaLabel={titleText}
          trackingEvent={trackingEvent}
          trackingEventKey={trackingEventKey}
          trackingEventValue={trackingEventValue}
        />
      )}
    </LayoutItem>
  )
}

export default Intro
